import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';

@Injectable({providedIn: 'root'})

export class DashboardService {
  constructor(private httpClient: HttpClient) {
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [meta, base64] = String(dataUrl || '').split(',');
    const mimeMatch = meta?.match(/data:(.*?);base64/);
    const mimeType = mimeMatch?.[1] || 'application/octet-stream';
    const binary = atob(base64 || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], {type: mimeType});
  }

  private normalizeUploadInput(data: any = {}): FormData | any {
    const candidate = data?.file ?? data;

    if (candidate instanceof File || candidate instanceof Blob) {
      const formData = new FormData();
      const fileName = candidate instanceof File ? candidate.name : `upload-${Date.now()}.bin`;
      formData.append('file', candidate, fileName);
      return formData;
    }

    if (typeof candidate === 'string' && candidate.startsWith('data:')) {
      const blob = this.dataUrlToBlob(candidate);
      const ext = blob.type?.split('/')[1] || 'bin';
      const formData = new FormData();
      formData.append('file', blob, `upload-${Date.now()}.${ext}`);
      return formData;
    }

    return data;
  }

  private objectToQueryString(obj: any): string {
    const keyValuePairs = [];
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (value !== null && value !== undefined && value !== '') {
          keyValuePairs.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
        }
      }
    }
    return keyValuePairs.join('&');
  }

  readAllUsers(query: any = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get('users' + (queryString ? `?${queryString}` : ''));
  }

  approveUser(id: string, data: any = {}): Observable<any> {
    return this.httpClient.post(`admin/users/${id}`, data);
  }

  getCurrentUserData(): Observable<any> {
    return this.httpClient.get('admin');
  }

  getUserDataById(id: string): Observable<any> {
    return this.httpClient.get(`user/${id}`);
  }

  deleteUserById(id: string): Observable<any> {
    return this.httpClient.delete(`user-id/${id}`);
  }

  updateUser(id, data): Observable<any> {
    return this.httpClient.patch(`admin/users/${id}`, data);
  }

  readAllEntities(data: any = {}): Observable<any> {
    return this.httpClient.post('entities', data);
  }

  getAllCategories(sorted): Observable<any> {
    return this.httpClient.get('categories' + sorted);
  }

  getInfoParamsByCategory(id: string): Observable<any> {
    return this.httpClient.get(`informative-parameter/${id}`);
  }

  getSubParamsByCategory(id: string): Observable<any> {
    return this.httpClient.get(`sub-parameters/${id}`);
  }

  createEntity(data): Observable<any> {
    return this.httpClient.post('entity', data);
  }

  createCategory(data): Observable<any> {
    return this.httpClient.post('category', data);
  }

  getInformativeData(id): Observable<any> {
    return this.httpClient.get(`info-data/${id}`);
  }

  createRateCategory(data): Observable<any> {
    return this.httpClient.post('rating-category', data);
  }

  imageUpload(image: File, entityId: string, path: string): Observable<any> {
    const formData = new FormData();
    formData.append('name', 'profile');
    formData.append('path', path);
    formData.append('id', entityId);
    formData.append('profile', image);
    return this.httpClient.post('image', formData, {});
  }

  uploadImage(data): Observable<any> {
    return this.httpClient.post('file-upload', this.normalizeUploadInput(data));
  }

  updateEntity = (data): Observable<any> => this.httpClient.patch(`update-entity/${data.id}`, data);
  deleteEntity = (id): Observable<any> => this.httpClient.delete(`entity/${id}`);
  deleteCategory = (id): Observable<any> => this.httpClient.delete(`category/${id}`);
  getSubjectiveData = (id): Observable<any> => this.httpClient.get(`subjective-data/${id}`);

  getSingleCategory = (id): Observable<any> => this.httpClient.get(`category/${id}`);

  getSingleEntity = (id): Observable<any> => this.httpClient.get(`entity/${id}`);

  updateCategory = (data, id): Observable<any> => this.httpClient.patch(`category/${id}`, data);

  updateInformativeParam(data, id): Observable<any> {
    return this.httpClient.patch(`informative-parameter/${id}`, data);
  }

  updateSubjectiveParam(data, id): Observable<any> {
    return this.httpClient.patch(`sub-parameter/${id}`, data);
  }

  // updateInfoData(data, id): Observable<any> {
  //   return this.httpClient.patch(`info-data/${id}`, data);
  // }
  //
  // updateSubjectiveData(data, id): Observable<any> {
  //   return this.httpClient.patch(`subjective-data/${id}`, data);
  // }

  getRatingsByUser(user): Observable<any> {
    return this.httpClient.get(`ratings-user/${user}`);
  }

  getUserTimeline(user): Observable<any> {
    return this.httpClient.get(`users/${user}/timeline?limit=1000`);
  }

  getRatingsScore(id): Observable<any> {
    return this.httpClient.get(`ratings-calculated/${id}`);
  }

  deleteRatings(id: string): Observable<any> {
    return this.httpClient.delete(`rating/${id}`);
  }

  dashboardCount(): Observable<any> {
    return this.httpClient.get('dashboard-count');
  }

  dashboardOverview(): Observable<any> {
    return this.httpClient.get('admin/dashboard/overview');
  }

  sortedRating(): Observable<any> {
    return this.httpClient.get('ratings-sorted');
  }

  getGraphData(id: string): Observable<any> {
    return this.httpClient.get(`ratings-graph/${id}`);
  }

  addArticle(data, image: File): Observable<any> {
    const formData = new FormData();
    formData.append('admin', data.admin);
    formData.append('title', data.title);
    formData.append('fieldType', data.type);
    formData.append('shortDescription', data.shortDescription);
    formData.append('description', data.description);
    formData.append('thumbnail', image);
    return this.httpClient.post('news', formData, {
      // headers: {
      //   'Content-Type': undefined
      // }
    });
  }

  getNewsArticles(data): Observable<any> {
    return this.httpClient.post('get-news', data);
  }

  getSingleArticle(id: string): Observable<any> {
    return this.httpClient.get(`article/${id}`);
  }

  removeArticle(id: string): Observable<any> {
    return this.httpClient.delete(`news/${id}`);
  }

  getAllSuggestions(): Observable<any> {
    return this.httpClient.get('suggestions');
  }

  createRatingParameters(data: any): Observable<any> {
    return this.httpClient.post('rating-parameters', data);
  }

  removeRatingCategory(id: string): Observable<any> {
    return this.httpClient.delete(`rating-category/${id}`);
  }

  getRatingCategory(id: string): Observable<any> {
    return this.httpClient.get(`get-rating-category/${id}`);
  }

  updateRatingCategory(id: string, data: any): Observable<any> {
    return this.httpClient.patch(`rating-category/${id}`, data);
  }

  getEntitiesByCategory(id): Observable<any> {
    return this.httpClient.get(`entities/${id}`);
  }

  searchEntities = (params): Observable<any> => this.httpClient.get(`v2/entities`, {params});

}
